/**
 * Application Constants
 * 
 * Centralized location for all hardcoded values used throughout the application
 * NOTE: Use functions for localized strings to avoid module-level evaluation issues
 */

// Contact Information (not localized - phone numbers)
export const SUPPORT_PHONE_DISPLAY = '+1 (844) 226-9226';
export const SUPPORT_PHONE_TEL = 'tel:+18442269226';

// Network Configuration (not localized - technical values)
export const DEFAULT_API_TIMEOUT_MS = 15000; // 15 seconds
export const DEFAULT_API_BASE_URL = process.env.REACT_APP_FUSUS_CORE_SETUP_API_BASE_URL || 'http://localhost:4000';
export const DEFAULT_VIDEO_LINK_DOMAIN = 'videolink.io';
export const DEFAULT_MIXPANEL_API_ENDPOINT = '/api/telemetry/mixpanel';
export const TRACKING_SESSION_ID = 'tracking_session_id';

// Tracking Sources (not localized - internal values)
export const SOURCE_QR_CODE = 'qr-code';
export const SOURCE_MODEL_RESOLVE = 'model-resolve';

// Statuses (not localized - internal values)
export const SUCCESS = 'success';
export const ERROR = 'error';
export const REACHABLE = 'reachable';
export const UNREACHABLE = 'unreachable';

// UI Timing (not localized - technical values)
export const QR_REDIRECT_DELAY_MS = 2000; // 2 seconds
export const DEFAULT_REDIRECT_DELAY_MS = 5000; // 5 seconds

// Asset Paths (not localized - file paths)
export const AXON_LOGO_PATH = '/assets/images/axon-logo.png';

// Localized Constants - Use functions to avoid module-level evaluation
// These return localized strings and should be called where needed
export const getAppTitle = () => __('Fusus Core Setup');
export const getErrorPageTitle = () => __('Page Not Found');
export const getErrorCode404 = () => '404';
export const getErrorBoundaryTitle = () => __('Oops! Something went wrong');
export const getSerialMissingError = (phone: string) => __('Device serial number is missing. Please scan the correct QR code or call our customer support team at $[phone] for assistance.', { phone });
export const getQrParamsError = () => __('Invalid QR code parameters');
export const getRedirectFailedError = () => __('Failed to redirect to device portal');
export const getSupportMessage = () => __('Need help completing setup? Our network support team is standing by to help at');
export const getContactSupportMessage = () => __('If this problem continues, please contact our support team at');
export const getNeedAssistanceMessage = () => __('Need assistance? Contact our support team at');

// Backward compatibility - these can be used as constants (not localized - internal values)
export const QR_PARAMS_ERROR = 'Invalid QR code parameters';
export const REDIRECT_FAILED_ERROR = 'Failed to redirect to device portal';

// Environment Validation Help Text (not localized - internal developer messages)
export const ENV_VALIDATION_HELP_TEXT = {
  REQUIRED_VARIABLES: 'Required variables:',
  OPTIONAL_VARIABLES: 'Optional variables:',
  RECAPTCHA_DESCRIPTION: 'Google reCAPTCHA site key',
  API_BASE_URL_DESCRIPTION: `Backend API base URL (defaults to ${DEFAULT_API_BASE_URL})`,
  ENV_DESCRIPTION: 'Application environment (development, production, test, staging)',
  ANALYTICS_DESCRIPTION: 'Enable analytics tracking (true/false)',
  DEBUG_DESCRIPTION: 'Enable debug mode (true/false)',
  CHECK_VARIABLES_MESSAGE: 'Please check your environment variables and ensure all required values are set.',
};

// Device URL Patterns - Shared between deviceCheck and guideDeviceCheck
export const DEVICE_URL_PATTERNS = {
  CLOUD: (serial: string) => `https://${serial}.videolink.io/net/api/provision`,
  LOCAL: (serial: string) => `https://${serial}.local/net/api/provision`,
  CLOUD_REDIRECT: (serial: string) => `https://${serial}.videolink.io/`,
  LOCAL_REDIRECT: (serial: string) => `https://${serial}.local/`,
} as const; 